/**
 * <ResetPasswordPage> — « Nouveau mot de passe », ouvert depuis le lien de réinitialisation (page).
 *
 * Trois champs : l'adresse du compte, le nouveau mot de passe, sa confirmation. On sort par une
 * croix : la page s'ouvre depuis un e-mail, elle n'est l'étape d'aucun parcours.
 *
 * Contrôlée : les valeurs, les messages de validation de chaque champ, le refus du serveur et
 * l'envoi en cours arrivent en props. Ce qui suit un enregistrement réussi n'est pas de cette page.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput, textInputAutofill } from "../../atoms/TextInput"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { AuthHeading } from "../../molecules/AuthHeading"
import { AuthTemplate } from "../../templates/AuthTemplate"

const TITLE = "Nouveau mot de passe"
const EMAIL_LABEL = "Adresse email"
const PASSWORD_LABEL = "Nouveau mot de passe"
const CONFIRMATION_LABEL = "Confirmer mot de passe"
const SUBMIT_LABEL = "Enregistrer"

export type ResetPasswordPageProps = {
    email: string
    password: string
    passwordConfirmation: string
    onChangeEmail: (email: string) => void
    onChangePassword: (password: string) => void
    onChangePasswordConfirmation: (passwordConfirmation: string) => void
    /** Les messages de validation, champ par champ, posés par le câblage à l'envoi. */
    emailError?: string
    passwordError?: string
    passwordConfirmationError?: string
    /** Le refus du serveur, dans ses mots (un lien périmé, une adresse inconnue). */
    serverError?: string
    /** L'enregistrement est parti : le bouton tourne et ne répond plus. */
    submitting?: boolean
    onSubmit: () => void
    /** La croix : quitter sans changer de mot de passe. */
    onClose?: () => void
    testID?: string
}

export function ResetPasswordPage(props: ResetPasswordPageProps) {
    const {
        email,
        password,
        passwordConfirmation,
        onChangeEmail,
        onChangePassword,
        onChangePasswordConfirmation,
        emailError,
        passwordError,
        passwordConfirmationError,
        serverError,
        submitting = false,
        onSubmit,
        onClose,
        testID,
    } = props

    // La touche « OK » du clavier ne passe pas par le bouton, qui seul se bloque pendant l'envoi.
    const submit = () => {
        if (!submitting) onSubmit()
    }

    return (
        <AuthTemplate onBack={onClose} leading="close" testID={testID}>
            <Stack gap="6">
                <AuthHeading title={TITLE} />

                <Stack gap="4">
                    <TextInput
                        {...textInputAutofill.email}
                        size="lg"
                        label={EMAIL_LABEL}
                        accessibilityLabel={EMAIL_LABEL}
                        value={email}
                        onChangeText={onChangeEmail}
                        error={emailError}
                    />
                    {/* Le préréglage « nouveau mot de passe » sur les DEUX champs : c'est ce qui fait proposer
                        un mot de passe fort par le gestionnaire, et le remplir dans la confirmation. */}
                    <TextInput
                        {...textInputAutofill.newPassword}
                        size="lg"
                        label={PASSWORD_LABEL}
                        accessibilityLabel={PASSWORD_LABEL}
                        value={password}
                        onChangeText={onChangePassword}
                        error={passwordError}
                    />
                    <TextInput
                        {...textInputAutofill.newPassword}
                        size="lg"
                        label={CONFIRMATION_LABEL}
                        accessibilityLabel={CONFIRMATION_LABEL}
                        value={passwordConfirmation}
                        onChangeText={onChangePasswordConfirmation}
                        error={passwordConfirmationError}
                        returnKeyType="done"
                        onSubmitEditing={submit}
                    />
                </Stack>

                {serverError != null && (
                    <ErrorText center live>
                        {serverError}
                    </ErrorText>
                )}

                <Button label={SUBMIT_LABEL} fullWidth loading={submitting} onPress={submit} />
            </Stack>
        </AuthTemplate>
    )
}
